import { ProjectIcon } from "./ProjectIcon";
import { useRef, useState, type RefObject } from "react";
import { FolderIcon, SearchIcon, SettingsIcon } from "lucide-react";
import { Popover, PopoverPopup, PopoverTrigger } from "./ui/popover";
import { SidebarHeaderIconButton } from "./sidebar/SidebarThreadHeader";

export interface ProjectPickerEntry {
  projectKey: string;
  displayName: string;
  icon?: string | undefined;
}
export function SidebarProjectPicker({
  projects,
  selectedKey,
  onSelect,
  onSettings,
  anchorRef,
}: {
  projects: readonly ProjectPickerEntry[];
  selectedKey: string | null;
  onSelect: (key: string | null) => void;
  onSettings: (key: string) => void;
  /**
   * The header's search field. The popup opens under it and is at least as
   * wide as it, rather than clinging to the 28px icon that triggered it.
   */
  anchorRef?: RefObject<HTMLDivElement | null>;
}) {
  const listRef = useRef<HTMLDivElement>(null);
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState("");
  const selected = projects.find((project) => project.projectKey === selectedKey);
  const visible = projects.filter((project) =>
    project.displayName.toLowerCase().includes(query.trim().toLowerCase()),
  );
  return (
    <Popover
      open={open}
      onOpenChange={(value) => {
        setOpen(value);
        if (!value) setQuery("");
      }}
    >
      <PopoverTrigger
        render={
          <SidebarHeaderIconButton
            label={
              selected
                ? `Filter threads by project: ${selected.displayName}`
                : "Filter threads by project"
            }
          />
        }
      >
        {selected?.icon ? (
          <ProjectIcon icon={selected.icon} />
        ) : (
          <FolderIcon className="size-4 shrink-0" />
        )}
      </PopoverTrigger>
      <PopoverPopup
        align="start"
        {...(anchorRef ? { anchor: anchorRef } : {})}
        className="min-w-[var(--anchor-width)] max-w-[min(18rem,var(--available-width))]"
        viewportClassName="p-1"
      >
        <div className="mx-2 mb-1 flex items-center gap-2 border-b py-2">
          <SearchIcon className="size-4 shrink-0 text-muted-foreground" />
          <input
            onKeyDown={(event) => {
              if (event.nativeEvent.isComposing) return;
              if (event.key === "Enter" && visible[0]) {
                event.preventDefault();
                onSelect(visible[0].projectKey);
                setOpen(false);
              }
              if (event.key === "ArrowDown" || event.key === "ArrowUp") {
                event.preventDefault();
                const buttons = listRef.current?.querySelectorAll<HTMLButtonElement>(
                  "button[data-project-choice]",
                );
                buttons?.[event.key === "ArrowDown" ? 0 : buttons.length - 1]?.focus();
              }
            }}
            aria-label="Search projects"
            placeholder="Search projects..."
            value={query}
            onChange={(event) => setQuery(event.target.value)}
            className="min-w-0 flex-1 bg-transparent text-sm outline-none"
          />
        </div>
        <div
          ref={listRef}
          className="max-h-72 overflow-y-auto"
          onKeyDown={(event) => {
            if (event.key !== "ArrowDown" && event.key !== "ArrowUp") return;
            const buttons = Array.from(
              listRef.current?.querySelectorAll<HTMLButtonElement>("button[data-project-choice]") ??
                [],
            );
            const index = buttons.indexOf(document.activeElement as HTMLButtonElement);
            if (index >= 0) {
              event.preventDefault();
              buttons[
                (index + (event.key === "ArrowDown" ? 1 : -1) + buttons.length) % buttons.length
              ]?.focus();
            }
          }}
        >
          {selected && !query ? (
            <button
              type="button"
              className="flex h-8 w-full items-center gap-2 rounded px-2 text-sm hover:bg-accent"
              onClick={() => {
                onSelect(null);
                setOpen(false);
              }}
            >
              <FolderIcon className="size-4" />
              All projects
            </button>
          ) : null}
          {visible.map((project) => (
            <div
              key={project.projectKey}
              className="flex items-center rounded hover:bg-accent focus-within:bg-accent"
            >
              <button
                type="button"
                data-project-choice
                aria-pressed={selectedKey === project.projectKey}
                className="flex h-8 min-w-0 flex-1 items-center gap-2 px-2 text-sm"
                onClick={() => {
                  onSelect(project.projectKey);
                  setOpen(false);
                }}
              >
                <ProjectIcon icon={project.icon} />
                <span className="truncate">{project.displayName}</span>
              </button>
              <button
                type="button"
                aria-label={`Settings for ${project.displayName}`}
                className="size-7 shrink-0 rounded p-1.5 text-muted-foreground hover:text-foreground"
                onClick={() => {
                  onSettings(project.projectKey);
                  setOpen(false);
                }}
              >
                <SettingsIcon className="size-3.5" />
              </button>
            </div>
          ))}
          {visible.length === 0 ? (
            <p className="p-3 text-xs text-muted-foreground">No matching projects.</p>
          ) : null}
        </div>
      </PopoverPopup>
    </Popover>
  );
}
